import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import moment from 'moment';
import {
  Badge, Button, Icon, Popover, Table, Tag, Tooltip
} from 'antd';
import settings from 'settings';
import { canViewOptimiser } from 'library/optimiser';
import OptimiserStatusApi from 'api/optimiserStatus';
import { loadSummary as loadSummaryAction } from 'actions/optimiserStatus';
import { loadBrokerHealth as loadBrokerHealthAction } from 'actions/solaceHealth';
import { loadRunStatus as loadRunStatusAction } from 'actions/optimiserRunStatus';
import { STATUS_PATH, runResultLabel } from 'views/OptimiserStatus/config';
import OptimiserRunIcon from './OptimiserRunIcon';
import { summariseOptimiserStatus, TONE_COLOURS } from './statusSummary';

// Re-export: views/OptimiserStatus/page.jsx and existing tests import this
// from the component module.
export { effectiveRunState } from './statusSummary';

const CROLogo = require('assets/images/logo/CRO Logo.png');

// At runtime the axios response interceptor wraps response.data as an Immutable
// structure; under tests it stays plain JS. Convert to plain JS either way.
const toPlain = data => (data && typeof data.toJS === 'function' ? data.toJS() : data);

/* Inject the flash keyframe once at module load — no SCSS file in this component. */
if (typeof document !== 'undefined' && !document.getElementById('solace-flash-style')) {
  const s = document.createElement('style');
  s.id = 'solace-flash-style';
  s.textContent = `
    @keyframes solace-flash { 0%,100%{opacity:1} 50%{opacity:0.15} }
    .bhp-optimiser-popover-run-icon * { animation: none !important; }
  `;
  document.head.appendChild(s);
}

const FLASH_ANIMATION = 'solace-flash 1.4s ease-in-out infinite';

// Compact popover view of the latest terminal runs — Time + Result only. Rows
// come straight from the run-history API (snake_case), same colouring as the
// status page's Result column.
export const buildPopoverRunColumns = () => [
  {
    title: 'Time',
    dataIndex: 'received_at',
    key: 'received_at',
    render: text => (text ? moment(text).format('DD MMM YYYY HH:mm') : '—')
  },
  {
    title: 'Result',
    dataIndex: 'result',
    key: 'result',
    render: (result) => {
      const meta = runResultLabel(result);
      return <Tag color={meta.tag}>{meta.label}</Tag>;
    }
  }
];

// Persistent outstanding-failure-count indicator for editors of an Optimiser
// tenancy (FR-6.2). It links straight to the status page. We only render and
// fetch when the current tenancy actually uses the Optimiser, so other tenancies
// never see it or trigger a validation run.
//
// The popover's failing-record list is fetched into LOCAL component state via the
// API directly (not the shared `optimiserStatus.violations` redux slice). This
// keeps opening the popover from clobbering the status page's own paginated
// table when both are mounted at once.
export class OptimiserStatusBadge extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      visible: false,
      popoverViolations: [],
      popoverLoading: false,
      popoverRunHistory: [],
      popoverRunHistoryLoading: false
    };
    this.handleVisibleChange = this.handleVisibleChange.bind(this);
  }

  componentDidMount() {
    const {
      enabled, loadSummary, loadBrokerHealth, loadRunStatus
    } = this.props;
    if (enabled) {
      loadSummary();
      loadBrokerHealth();
      loadRunStatus();
      this.pollId = setInterval(() => {
        loadBrokerHealth();
        loadRunStatus();
      }, 10000);
    }
  }

  componentWillUnmount() {
    if (this.pollId) {
      clearInterval(this.pollId);
    }
  }

  handleVisibleChange(visible) {
    const { loadSummary } = this.props;
    this.setState({ visible });
    if (!visible) {
      return;
    }
    // Refresh the redux summary count each time the popover opens so the badge
    // reflects the latest outstanding-failure total, not just the mount-time value.
    loadSummary();
    this.setState({ popoverLoading: true });
    OptimiserStatusApi.getViolations({ page: 0, pageSize: settings.APP_CONFIG.defaultTablePageSmallSize })
      .then((response) => {
        const data = toPlain(response.data) || {};
        this.setState({ popoverViolations: data.results || [], popoverLoading: false });
      })
      .catch(() => {
        this.setState({ popoverLoading: false });
      });
    this.setState({ popoverRunHistoryLoading: true });
    OptimiserStatusApi.getRunHistory()
      .then((response) => {
        const rows = toPlain(response.data);
        this.setState({
          popoverRunHistory: Array.isArray(rows) ? rows : [],
          popoverRunHistoryLoading: false
        });
      })
      .catch(() => {
        this.setState({ popoverRunHistoryLoading: false });
      });
  }

  render() {
    const {
      enabled, count, classes, brokerHealth, runStatus, acknowledgedRuns
    } = this.props;
    const {
      visible, popoverViolations, popoverLoading, popoverRunHistory, popoverRunHistoryLoading
    } = this.state;
    if (!enabled) {
      return null;
    }

    const summary = summariseOptimiserStatus({
      runStatus, count, brokerHealth, acknowledgedRuns
    });

    const columns = [
      {
        title: 'Record', dataIndex: 'record_label', key: 'record_label', width: '180px'
      },
      {
        title: 'Error', dataIndex: 'error_type', key: 'error_type', width: '120px'
      },
      {
        title: 'Detail', dataIndex: 'detail', key: 'detail'
      }
    ];

    const iconFor = (line) => {
      if (line.key === 'broker') {
        return <Icon type="wifi" style={{ fontSize: '10px', color: '#fff' }} />;
      }
      return <Icon type={line.tone === 'ok' ? 'check' : 'exclamation'} style={{ fontSize: '10px', color: '#fff' }} />;
    };

    const statusRows = summary.lines.map(line => (
      <div
        key={line.key}
        className={`bhp-optimiser-status-line bhp-optimiser-status-line--${line.key}`}
        style={{ display: 'flex', alignItems: 'center', marginBottom: 6 }}
      >
        {line.key === 'run' ? (
          <span className="bhp-optimiser-popover-run-icon" style={{ display: 'inline-flex', marginRight: 8, flexShrink: 0 }}>
            <OptimiserRunIcon state={summary.runState} size={16} />
          </span>
        ) : (
          <span style={{
            width: '16px',
            height: '16px',
            borderRadius: '50%',
            backgroundColor: TONE_COLOURS[line.tone],
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginRight: 8,
            flexShrink: 0
          }}
          >
            {iconFor(line)}
          </span>
        )}
        <span>{line.text}</span>
      </div>
    ));

    const runSubRows = summary.runLines.map(line => (
      <div
        key={line.key}
        className="bhp-optimiser-status-subline"
        style={{
          display: 'flex', alignItems: 'center', margin: '0 0 4px 24px', color: 'rgba(0,0,0,0.65)'
        }}
      >
        <span style={{
          width: 6, height: 6, borderRadius: '50%', backgroundColor: TONE_COLOURS[line.tone], marginRight: 8, flexShrink: 0
        }}
        />
        <span>{line.text}</span>
      </div>
    ));

    const content = (
      <div>
        <div style={{ marginBottom: 12 }}>
          {statusRows}
          {runSubRows}
        </div>
        {popoverRunHistory.length > 0 && (
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>Recent runs</div>
            <Table
              rowKey={record => record.id}
              dataSource={popoverRunHistory.slice(0, 3)}
              columns={buildPopoverRunColumns()}
              loading={popoverRunHistoryLoading}
              pagination={false}
              size="small"
              className="bhp-optimiser-popover-run-history"
            />
          </div>
        )}
        {summary.count > 0 && (
          <Table
            rowKey={record => `${record.record_type}-${record.record_id}-${record.error_type}`}
            dataSource={popoverViolations}
            columns={columns}
            loading={popoverLoading}
            pagination={false}
            size="small"
            className="bhp-notifications-popup"
          />
        )}
        <div style={{ marginTop: 8, textAlign: 'right' }}>
          <Link to={STATUS_PATH}>
            <Button size="small" type="primary" onClick={() => this.setState({ visible: false })}>
              Open full page
            </Button>
          </Link>
        </div>
      </div>
    );

    const tooltipRunLines = summary.runLines.slice(0, 3);
    const tooltipTitle = (
      <div>
        {summary.lines.map(line => <div key={line.key}>{line.text}</div>)}
        {tooltipRunLines.map(line => <div key={line.key}>{line.text}</div>)}
        {summary.runLines.length > 3 && (
          <div>{`+${summary.runLines.length - 3} more`}</div>
        )}
      </div>
    );

    return (
      <Popover
        content={content}
        placement="bottom"
        title="Optimiser Status"
        trigger="click"
        visible={visible}
        onVisibleChange={this.handleVisibleChange}
      >
        <Tooltip title={tooltipTitle}>
          <div className={classes} id="OptimiserStatusBadge">
            <span style={{ position: 'relative', display: 'inline-block' }}>
              <OptimiserRunIcon state={summary.runState} />
              <img src={CROLogo} alt="Optimiser" style={{ height: '31px', width: 'auto', display: 'inline-block' }} />
              {summary.alert === 'count' && (
                <span
                  className="bhp-optimiser-alert-count"
                  style={{ animation: summary.flashing ? FLASH_ANIMATION : 'none' }}
                >
                  <Badge showZero={false} overflowCount={99} offset={[-5, -5]} count={summary.count} />
                </span>
              )}
              {summary.alert === 'dot' && (
                <span
                  className="bhp-optimiser-alert-dot"
                  style={{
                    position: 'absolute',
                    top: '-4px',
                    right: '-4px',
                    width: '11px',
                    height: '11px',
                    borderRadius: '50%',
                    backgroundColor: TONE_COLOURS.error,
                    border: '1px solid #fff',
                    animation: summary.flashing ? FLASH_ANIMATION : 'none'
                  }}
                />
              )}
            </span>
          </div>
        </Tooltip>
      </Popover>
    );
  }
}

OptimiserStatusBadge.propTypes = {
  enabled: PropTypes.bool,
  count: PropTypes.number,
  classes: PropTypes.string,
  loadSummary: PropTypes.func,
  loadBrokerHealth: PropTypes.func,
  loadRunStatus: PropTypes.func,
  brokerHealth: PropTypes.shape({
    state: PropTypes.string,
    recentFailureCount: PropTypes.number
  }),
  runStatus: PropTypes.shape({
    state: PropTypes.string,
    activeRuns: PropTypes.arrayOf(PropTypes.shape({
      runIdentifier: PropTypes.string,
      runName: PropTypes.string,
      state: PropTypes.string,
      triggeredAt: PropTypes.string,
      receivedAt: PropTypes.string
    })),
    recentTerminalRuns: PropTypes.arrayOf(PropTypes.shape({
      runIdentifier: PropTypes.string,
      state: PropTypes.string,
      receivedAt: PropTypes.string,
      finishedAt: PropTypes.string
    })),
    finishedWindowSeconds: PropTypes.number
  }),
  acknowledgedRuns: PropTypes.arrayOf(PropTypes.shape({
    runIdentifier: PropTypes.string,
    receivedAt: PropTypes.string
  }))
};

OptimiserStatusBadge.defaultProps = {
  enabled: false,
  count: 0,
  classes: '',
  loadSummary() {},
  loadBrokerHealth() {},
  loadRunStatus() {},
  brokerHealth: { state: 'unknown', recentFailureCount: 0 },
  runStatus: {
    state: 'unknown', activeRuns: [], recentTerminalRuns: [], finishedWindowSeconds: 600
  },
  acknowledgedRuns: []
};

// Delegate the badge's visibility decision to the shared helper so it shows during
// readiness_checking as well as enabled (reading currentTenancy.optimiserState with
// the legacy optimiserEnabled fallback) AND only for users who hold the optimiser
// view (or admin) permission. Kept as a named export for backwards compatibility
// with existing imports/tests.
export const shouldShowBadge = () => canViewOptimiser();

const mapStateToProps = state => ({
  enabled: shouldShowBadge(),
  count: state.optimiserStatus ? state.optimiserStatus.summary.total : 0,
  brokerHealth: state.solaceHealth ? state.solaceHealth.health : { state: 'unknown', recentFailureCount: 0 },
  runStatus: state.optimiserRunStatus ? state.optimiserRunStatus.runStatus : { state: 'unknown' },
  acknowledgedRuns: state.optimiserRunStatus && state.optimiserRunStatus.optimiserFinishedRuns
    ? state.optimiserRunStatus.optimiserFinishedRuns
      .filter(r => r.acknowledged)
      .map(r => ({ runIdentifier: r.runIdentifier, receivedAt: r.receivedAt }))
    : []
});

const mapDispatchToProps = dispatch => ({
  loadSummary() {
    return dispatch(loadSummaryAction());
  },
  loadBrokerHealth() {
    return dispatch(loadBrokerHealthAction());
  },
  loadRunStatus() {
    return dispatch(loadRunStatusAction());
  }
});

export default connect(mapStateToProps, mapDispatchToProps)(OptimiserStatusBadge);
